import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { FileArrowDown, FileCsv, FileXls, Table } from "@phosphor-icons/react";
import { BarChart, Bar, ComposedChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

const REPORTS = [
  { key: "pending", label: "Daily Pending Tasks" },
  { key: "completed", label: "Completed Tasks" },
  { key: "overdue", label: "Overdue Tasks" },
  { key: "ageing", label: "Task Ageing" },
  { key: "reopened", label: "Reopened Tasks" },
  { key: "escalation", label: "Escalation & Overdue" },
  { key: "delay_analytics", label: "Delay Analytics" },
  { key: "delay_reasons", label: "Delay Reasons" },
  { key: "bottlenecks", label: "Parallel Bottlenecks" },
  { key: "stage_duration", label: "Manufacturing Stage Duration" },
  { key: "design_skip_history", label: "Design Skip History" },
  { key: "employee_performance", label: "Employee Performance" },
  { key: "department_performance", label: "Department Performance" },
  { key: "vendor_spend", label: "Monthly Vendor Spend" },
  { key: "pending_material", label: "Pending Material (POs)" },
];

export default function Reports() {
  const [preview, setPreview] = useState(null);
  const [active, setActive] = useState(null);
  const [slowThreshold, setSlowThreshold] = useState(48);

  useEffect(() => {
    api.get("/settings/stage-duration").then((r) => setSlowThreshold(r.data.slow_threshold_hours)).catch(() => {});
  }, []);

  const view = async (r) => {
    setActive(r.key);
    try {
      const { data } = await api.get(`/reports/${r.key}?format=json`);
      setPreview({ label: r.label, rows: data.rows, trend: data.trend || null });
    } catch { toast.error("Failed to load report"); setPreview(null); }
  };

  const download = async (key, format) => {
    try {
      const res = await api.get(`/reports/${key}?format=${format}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `${key}_report.${format === "excel" ? "xlsx" : "csv"}`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch { toast.error("Download failed"); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px] animate-fade-in">
      <div className="text-[10px] uppercase tracking-[0.2em] text-emerald-600 font-bold mb-1">Insights</div>
      <h1 className="font-heading text-3xl font-black tracking-tight text-zinc-50 mb-6">Reports</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
        {REPORTS.map((r) => (
          <div key={r.key} className={`border p-4 transition-colors ${active === r.key ? "border-emerald-600 bg-emerald-600/5" : "border-zinc-800 bg-zinc-900/30 hover:border-zinc-700"}`} data-testid={`report-${r.key}`}>
            <div className="flex items-center gap-2 mb-3"><Table size={18} className="text-emerald-400" /><span className="text-sm text-zinc-100 font-medium">{r.label}</span></div>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => view(r)} className="border-zinc-800 bg-zinc-950 rounded-sm text-xs flex-1">View</Button>
              <Button size="sm" variant="outline" onClick={() => download(r.key, "csv")} className="border-zinc-800 bg-zinc-950 rounded-sm text-xs gap-1" data-testid={`dl-csv-${r.key}`}><FileCsv size={14} /> CSV</Button>
              <Button size="sm" variant="outline" onClick={() => download(r.key, "excel")} className="border-zinc-800 bg-zinc-950 rounded-sm text-xs gap-1" data-testid={`dl-xls-${r.key}`}><FileXls size={14} /> Excel</Button>
            </div>
          </div>
        ))}
      </div>

      {preview && (
        <div className="border border-zinc-800 bg-zinc-900/30">
          <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
            <span className="text-xs font-bold uppercase tracking-wider text-zinc-400">{preview.label} · {preview.rows.length} rows</span>
          </div>
          <div className="overflow-x-auto max-h-[500px]">
            {active === "delay_analytics" && preview.rows.length > 0 && (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 p-4 border-b border-zinc-800" data-testid="delay-charts">
                {[["Person", "Slowest People (by delays)"], ["Stage", "Slowest Stages (by delays)"]].map(([type, title]) => {
                  const data = preview.rows.filter((r) => r.Type === type).map((r) => ({ name: r.Name, count: r["Delay Count"] }));
                  return (
                    <div key={type}>
                      <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-3">{title}</div>
                      {data.length === 0 ? <div className="text-xs text-zinc-500">No delays recorded.</div> : (
                        <ResponsiveContainer width="100%" height={Math.max(160, data.length * 42)}>
                          <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#dde4d9" horizontal={false} />
                            <XAxis type="number" stroke="#55655a" fontSize={11} allowDecimals={false} />
                            <YAxis type="category" dataKey="name" stroke="#55655a" fontSize={11} width={110} />
                            <Tooltip cursor={{ fill: "#128a4914" }} contentStyle={{ background: "#ffffff", border: "1px solid #dde4d9", borderRadius: 4, fontSize: 12, color: "#17251c" }} />
                            <Bar dataKey="count" name="Delays" fill="#128a49" radius={[0, 3, 3, 0]} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {active === "delay_reasons" && preview.rows.length > 0 && (
              <div className="p-4 border-b border-zinc-800" data-testid="delay-reasons-chart">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Most recurring delay reasons</div>
                <p className="text-[11px] text-zinc-500 mb-3">Same wording logged across different orders/stages, grouped — the top bars are your recurring bottlenecks worth fixing at the source.</p>
                <ResponsiveContainer width="100%" height={Math.max(160, Math.min(preview.rows.length, 10) * 42)}>
                  <BarChart data={preview.rows.slice(0, 10).map((r) => ({ name: r.Reason, count: r["Times Logged"] }))} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#dde4d9" horizontal={false} />
                    <XAxis type="number" stroke="#55655a" fontSize={11} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" stroke="#55655a" fontSize={11} width={220} />
                    <Tooltip cursor={{ fill: "#128a4914" }} contentStyle={{ background: "#ffffff", border: "1px solid #dde4d9", borderRadius: 4, fontSize: 12, color: "#17251c" }} />
                    <Bar dataKey="count" name="Times logged" fill="#f97316" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
                {preview.trend && preview.trend.length > 0 && (
                  <div className="mt-6 pt-4 border-t border-zinc-800/60" data-testid="delay-reasons-trend-chart">
                    <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Delays logged per month</div>
                    <p className="text-[11px] text-zinc-500 mb-3">Total delay entries across all reasons/orders each month — a rising line means bottlenecks are getting worse, not better.</p>
                    <ResponsiveContainer width="100%" height={200}>
                      <ComposedChart data={preview.trend.map((t) => ({ month: t.month, count: t.count }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#dde4d9" vertical={false} />
                        <XAxis dataKey="month" stroke="#55655a" fontSize={11} />
                        <YAxis stroke="#55655a" fontSize={11} allowDecimals={false} />
                        <Tooltip formatter={(v) => [v, "Delays logged"]} contentStyle={{ background: "#ffffff", border: "1px solid #dde4d9", borderRadius: 4, fontSize: 12, color: "#17251c" }} />
                        <Line type="monotone" dataKey="count" name="Delays logged" stroke="#f97316" strokeWidth={2} dot={{ r: 4, fill: "#f97316" }} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            )}
            {active === "bottlenecks" && preview.rows.length > 0 && (
              <div className="p-4 border-b border-zinc-800" data-testid="bottleneck-chart">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Streams that finish last most often</div>
                <p className="text-[11px] text-zinc-500 mb-3">The department stream that ends a parallel block last is your bottleneck — add capacity there first.</p>
                <ResponsiveContainer width="100%" height={Math.max(160, preview.rows.length * 42)}>
                  <BarChart data={preview.rows.map((r) => ({ name: r["Stream / Stage"], last: r["Finished Last"], avg: r["Avg Hours"] || 0 }))} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#dde4d9" horizontal={false} />
                    <XAxis type="number" stroke="#55655a" fontSize={11} allowDecimals={false} />
                    <YAxis type="category" dataKey="name" stroke="#55655a" fontSize={11} width={190} />
                    <Tooltip cursor={{ fill: "#128a4914" }} contentStyle={{ background: "#ffffff", border: "1px solid #dde4d9", borderRadius: 4, fontSize: 12, color: "#17251c" }} />
                    <Bar dataKey="last" name="Finished last (count)" fill="#ef4444" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            {active === "vendor_spend" && preview.rows.length > 0 && (
              <div className="p-4 border-b border-zinc-800" data-testid="vendor-spend-chart">
                <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Purchase spend by month</div>
                <p className="text-[11px] text-zinc-500 mb-3">Total PO value placed each month across all vendors, with a trend line — the table below breaks it down vendor-wise.</p>
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={Object.entries(preview.rows.reduce((acc, r) => {
                    acc[r.Month] = (acc[r.Month] || 0) + (Number(r["Total Spend (Rs.)"]) || 0);
                    return acc;
                  }, {})).sort((a, b) => a[0].localeCompare(b[0])).map(([month, total]) => ({ month, total }))}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#dde4d9" vertical={false} />
                    <XAxis dataKey="month" stroke="#55655a" fontSize={11} />
                    <YAxis stroke="#55655a" fontSize={11} tickFormatter={(v) => `₹${v >= 100000 ? (v / 100000).toFixed(1) + "L" : v >= 1000 ? (v / 1000).toFixed(0) + "k" : v}`} />
                    <Tooltip cursor={{ fill: "#128a4914" }} formatter={(v) => [`₹ ${Number(v).toLocaleString("en-IN")}`, "Spend"]} contentStyle={{ background: "#ffffff", border: "1px solid #dde4d9", borderRadius: 4, fontSize: 12, color: "#17251c" }} />
                    <Bar dataKey="total" name="Spend" fill="#128a49" radius={[3, 3, 0, 0]} maxBarSize={64} />
                    <Line type="monotone" dataKey="total" name="Trend" stroke="#f97316" strokeWidth={2} dot={{ r: 4, fill: "#f97316" }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
            {active === "stage_duration" && preview.rows.length > 0 && (() => {
              const agg = {};
              preview.rows.forEach((r) => {
                const k = r.Stage;
                agg[k] = agg[k] || { total: 0, count: 0 };
                agg[k].total += Number(r["Hours Spent"]) || 0;
                agg[k].count += 1;
              });
              const data = Object.entries(agg).map(([name, v]) => ({ name, avg: Math.round((v.total / v.count) * 10) / 10 }))
                .sort((a, b) => b.avg - a.avg);
              return (
                <div className="p-4 border-b border-zinc-800" data-testid="stage-duration-chart">
                  <div className="text-xs font-bold uppercase tracking-wider text-zinc-400 mb-1">Average time per manufacturing stage</div>
                  <p className="text-[11px] text-zinc-500 mb-3">Stages in red are averaging more than the {slowThreshold}h "slow" threshold — adjustable in Company Settings &gt; Manufacturing Alerts.</p>
                  <ResponsiveContainer width="100%" height={Math.max(160, data.length * 42)}>
                    <BarChart data={data} layout="vertical" margin={{ left: 10, right: 20 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#dde4d9" horizontal={false} />
                      <XAxis type="number" stroke="#55655a" fontSize={11} unit="h" />
                      <YAxis type="category" dataKey="name" stroke="#55655a" fontSize={11} width={170} />
                      <Tooltip cursor={{ fill: "#128a4914" }} formatter={(v) => [`${v}h`, "Avg time"]} contentStyle={{ background: "#ffffff", border: "1px solid #dde4d9", borderRadius: 4, fontSize: 12, color: "#17251c" }} />
                      <Bar dataKey="avg" name="Avg Hours" radius={[0, 3, 3, 0]}>
                        {data.map((d, i) => <Cell key={i} fill={d.avg > slowThreshold ? "#ef4444" : "#128a49"} />)}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              );
            })()}
            {preview.rows.length === 0 ? (
              <div className="p-8 text-center text-zinc-500 text-sm">No data available.</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-zinc-950">
                  <tr className="border-b border-zinc-800 text-left text-[10px] uppercase tracking-wider text-zinc-500">
                    {Object.keys(preview.rows[0]).map((k) => <th key={k} className="px-4 py-2.5 font-bold whitespace-nowrap">{k}</th>)}
                  </tr>
                </thead>
                <tbody className="divide-y divide-zinc-800">
                  {preview.rows.map((row, i) => (
                    <tr key={i} className="hover:bg-zinc-900 transition-colors">
                      {Object.entries(row).map(([k, v]) => (
                        <td key={k} className={`px-4 py-2.5 whitespace-nowrap ${k === "Slow" && v === "Yes" ? "text-red-400 font-bold" : "text-zinc-300"}`}>{v ?? "—"}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
